import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { JSONSchemaProperty } from '../../WindmillAppPage/types';

import styles from './FieldOrderEditor.module.scss';

interface FieldOrderEditorProps {
  /** Current order — always exactly the keys of `properties`, no duplicates. */
  fieldOrder: string[];
  properties: Record<string, JSONSchemaProperty>;
  onChange: (next: string[]) => void;
  onReset: () => void;
}

/**
 * Reorders the app's input fields with up/down buttons. The list is a permutation
 * of the current schema keys — there is no free-text entry — so it cannot produce
 * a duplicate or an unknown field name.
 */
export const FieldOrderEditor: React.FC<FieldOrderEditorProps> = ({
  fieldOrder,
  properties,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation();

  const move = useCallback(
    (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= fieldOrder.length) return;
      const next = [...fieldOrder];
      next[index] = fieldOrder[target];
      next[target] = fieldOrder[index];
      onChange(next);
    },
    [fieldOrder, onChange],
  );

  if (fieldOrder.length === 0) {
    return (
      <p className={styles.empty}>
        {t('No input fields in the current schema.')}
      </p>
    );
  }

  return (
    <div className={styles.root}>
      <ol className={styles.list}>
        {fieldOrder.map((key, index) => (
          <li key={key} className={styles.row}>
            <span className={styles.position}>{index + 1}</span>
            <span className={styles.name}>
              {properties[key]?.title || key}
              {properties[key]?.title && (
                <span className={styles.key}>{key}</span>
              )}
            </span>
            <span className={styles.actions}>
              <button
                type="button"
                className="btn-reset"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                title={t('Move up') as string}
                aria-label={t('Move up') as string}
              >
                <i className="las la-arrow-up" />
              </button>
              <button
                type="button"
                className="btn-reset"
                onClick={() => move(index, 1)}
                disabled={index === fieldOrder.length - 1}
                title={t('Move down') as string}
                aria-label={t('Move down') as string}
              >
                <i className="las la-arrow-down" />
              </button>
            </span>
          </li>
        ))}
      </ol>
      <button type="button" className="btn-reset" onClick={onReset}>
        {t('Reset to schema order')}
      </button>
    </div>
  );
};
